import { useAtom, useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useCallback, useEffect, useState } from 'react';
import { Slider } from '@mui/material';
import FormLabel from '@mui/material/FormLabel';
import { ToggleItemVisibility } from '../ToggleItemVisibility';
import { useKey } from '@/hooks';

export const AnnotationControls = () => {
  const selectedSpherical = useAtomValue(state.selectedSpherical);
  const [showPointOfInterestAnnotations, setShowPointOfInterestAnnotations] = useAtom(
    state.showPointOfInterestAnnotations
  );
  const [annotationScale, setAnnotationScale] = useAtom<number>(state.annotationScale);
  const [swivelAnnotations, setSwivelAnnotations] = useAtom<boolean>(state.swivelAnnotations);
  const [annotationHeightVariance, setAnnotationHeightVariance] = useAtom<number>(
    state.annotationHeightVariance
  );
  const [unfinishedScaleValue, setUnfinishedScaleValue] = useState(annotationScale);
  const [unfinishedHeightVarianceValue, setUnfinishedHeightVarianceValue] =
    useState(annotationHeightVariance);

  useEffect(() => {
    if (selectedSpherical) {
      setSwivelAnnotations(false);
      setUnfinishedHeightVarianceValue(0);
    } else {
      setUnfinishedHeightVarianceValue(5);
    }
  }, [selectedSpherical, setSwivelAnnotations, setAnnotationHeightVariance]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setAnnotationScale(unfinishedScaleValue);
    }, 250);

    return () => clearTimeout(debounce);
  }, [setAnnotationScale, unfinishedScaleValue]);

  const handleScaleValueChanged = (event: Event, value: number | number[]) => {
    setUnfinishedScaleValue(value as number);
  };

  const handleHeightVarianceValueChanged = (event: Event, value: number | number[]) => {
    setUnfinishedHeightVarianceValue(value as number);
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      setAnnotationHeightVariance(unfinishedHeightVarianceValue);
    }, 250);

    return () => clearTimeout(debounce);
  }, [setAnnotationHeightVariance, unfinishedHeightVarianceValue]);

  const toggleSwivelAnnotations = () => {
    setSwivelAnnotations((prev) => !prev);
  };

  const togglePointOfInterestAnnotations = useCallback(() => {
    setShowPointOfInterestAnnotations((current) => !current);
  }, [setShowPointOfInterestAnnotations]);

  useKey('l', togglePointOfInterestAnnotations, 'alt');
  useKey('o', toggleSwivelAnnotations, 'alt');

  return (
    <>
      <ToggleItemVisibility
        label="Show all annotations"
        isVisible={showPointOfInterestAnnotations}
        toggleVisibility={togglePointOfInterestAnnotations}
        tooltipTitle="'Press Alt + L'"
      />
      {showPointOfInterestAnnotations && (
        <>
          <div>
            <FormLabel>Annotation Scale</FormLabel>
            <Slider
              min={0.5}
              step={0.5}
              max={10}
              aria-label="Annotation Scale"
              getAriaValueText={(value) => `${value}`}
              valueLabelDisplay="auto"
              value={unfinishedScaleValue}
              onChange={handleScaleValueChanged}
              size="small"
            />
          </div>
          <div>
            <FormLabel>Annotation Height Variance</FormLabel>
            <Slider
              min={0}
              step={1}
              max={10}
              aria-label="Annotation Height Variance"
              getAriaValueText={(value) => `${value}`}
              valueLabelDisplay="auto"
              value={unfinishedHeightVarianceValue}
              onChange={handleHeightVarianceValueChanged}
              size="small"
            />
          </div>
          <ToggleItemVisibility
            label="Switch Annotation Orientation"
            isVisible={swivelAnnotations}
            toggleVisibility={toggleSwivelAnnotations}
            tooltipTitle="'Press Alt + O'"
          />
        </>
      )}
    </>
  );
};
